import { useState, useRef, useEffect } from "react";
import { DemoHint } from "@/components/demo/DemoHint";
import { Upload, ChevronRight, ChevronDown, Download, CheckCircle2, AlertTriangle, X, Loader2, Save, Trash2, FolderOpen, Ban } from "lucide-react";
import { PostLoadCard } from "@/components/post-load/PostLoadCard";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";
import {
  downloadTemplateCSV,
  parseAndValidateCSV,
  geocodeCity,
  buildLoadPayload,
  validateRow,
  type BulkParseResult,
  type BulkRow,
  BULK_CSV_HEADERS,
} from "@/lib/bulkUploadSchema";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SOFT_WARN_ROWS = 50;       // show "large upload" warning
const MAX_BULK_ROWS = 100;       // hard cap for now (temporary UI safeguard)
const ROW_TIMEOUT_MS = 15_000;   // per-row timeout

// TODO: Phase 2 — move bulk posting to a server-side edge function
// for parallel inserts, job status polling, and browser-disconnect resilience.
// Current browser-side sequential approach is a temporary safeguard.

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PostResult {
  posted: number;
  skipped: number;
  failed: { rowNumber: number; error: string }[];
  cancelled: number;
  total: number;
}

interface SavedBulkTemplate {
  id: string;
  name: string;
  row_count: number;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  rows: Record<string, string>[];
}

interface PostingProgress {
  current: number;
  total: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Wrap a promise with a timeout — rejects if not resolved within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Row timed out after ${ms / 1000} seconds`)), ms),
    ),
  ]);
}

/** Build a geocode cache key from city + state */
function geoKey(city: string, state: string): string {
  return `${city.trim().toLowerCase()}|${state.trim().toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function BulkUploadCard() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<BulkParseResult | null>(null);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState<PostResult | null>(null);
  const [postingProgress, setPostingProgress] = useState<PostingProgress | null>(null);
  const [rowLimitWarning, setRowLimitWarning] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { showWarningOrRun, modalProps } = useVerificationWarning();

  // Saved templates state
  const [savedTemplates, setSavedTemplates] = useState<SavedBulkTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  /* ------------------------------------------------------------------ */
  /*  Fetch saved templates                                              */
  /* ------------------------------------------------------------------ */

  const fetchSavedTemplates = async () => {
    if (!user) return;
    setLoadingTemplates(true);
    try {
      const { data, error } = await supabase
        .from("bulk_templates" as any)
        .select("id, name, row_count, usage_count, last_used_at, created_at, rows")
        .eq("created_by", user.id)
        .order("usage_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(20);
      if (!error && data) setSavedTemplates(data as any[]);
    } catch {
      /* silent */
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (open && user) fetchSavedTemplates();
  }, [open, user]);

  /* ------------------------------------------------------------------ */
  /*  Row-limit check helper                                             */
  /* ------------------------------------------------------------------ */

  /** Returns true if the row count is acceptable, false if blocked. */
  const checkRowLimit = (totalRows: number): boolean => {
    if (totalRows > MAX_BULK_ROWS) {
      toast({
        title: `Maximum ${MAX_BULK_ROWS} rows per upload`,
        description: "Please split your file into smaller batches.",
        variant: "destructive",
      });
      setRowLimitWarning(null);
      return false;
    }
    if (totalRows > SOFT_WARN_ROWS) {
      setRowLimitWarning(`Large upload (${totalRows} rows) — processing may take 30+ seconds`);
    } else {
      setRowLimitWarning(null);
    }
    return true;
  };

  /* ------------------------------------------------------------------ */
  /*  CSV handlers                                                       */
  /* ------------------------------------------------------------------ */

  const handleDownload = () => {
    downloadTemplateCSV();
    toast({ title: "Template downloaded", description: "Fill in your loads and upload the CSV." });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parsed = parseAndValidateCSV(text);

      if (!checkRowLimit(parsed.totalRows)) {
        e.target.value = "";
        return;
      }

      setResult(parsed);
      setPostResult(null);
      setShowSaveInput(false);
      toast({
        title: `${parsed.totalRows} row${parsed.totalRows !== 1 ? "s" : ""} parsed`,
        description: `${parsed.validRows.length} valid · ${parsed.invalidRows.length} invalid`,
      });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const clearAll = () => {
    setResult(null);
    setPostResult(null);
    setPostingProgress(null);
    setRowLimitWarning(null);
    setShowSaveInput(false);
    setSaveName("");
    cancelRef.current = false;
  };

  /* ------------------------------------------------------------------ */
  /*  Posting logic — with geocode dedup, progress, timeout, cancel      */
  /* ------------------------------------------------------------------ */

  const handlePost = async () => {
    if (!result || result.validRows.length === 0 || !user) return;
    setPosting(true);
    setPostingProgress({ current: 0, total: result.validRows.length });
    cancelRef.current = false;

    let companyId: string | null = null;
    try {
      const { data } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      if (data && data.length > 0) companyId = data[0];
    } catch { /* optional */ }

    if (!companyId) {
      toast({ title: "No company found", description: "You need to be part of a verified company to post loads.", variant: "destructive" });
      setPosting(false);
      setPostingProgress(null);
      return;
    }

    /* --- Batch geocode deduplication --- */
    const uniqueKeys = new Set<string>();
    for (const row of result.validRows) {
      uniqueKeys.add(geoKey(row.data.origin_city, row.data.origin_state));
      uniqueKeys.add(geoKey(row.data.destination_city, row.data.destination_state));
    }

    const geoCache = new Map<string, Awaited<ReturnType<typeof geocodeCity>>>();
    for (const key of uniqueKeys) {
      if (cancelRef.current) break;
      const [city, state] = key.split("|");
      try {
        const geo = await geocodeCity(city, state);
        geoCache.set(key, geo);
      } catch {
        geoCache.set(key, null);
      }
    }

    /* --- Row-by-row posting --- */
    const failed: { rowNumber: number; error: string }[] = [];
    let posted = 0;
    let cancelled = 0;

    for (let i = 0; i < result.validRows.length; i++) {
      if (cancelRef.current) {
        cancelled = result.validRows.length - i;
        break;
      }

      const row = result.validRows[i];
      setPostingProgress({ current: i + 1, total: result.validRows.length });

      try {
        await withTimeout(
          (async () => {
            const originKey = geoKey(row.data.origin_city, row.data.origin_state);
            const destKey = geoKey(row.data.destination_city, row.data.destination_state);

            const origin = geoCache.get(originKey) ?? null;
            if (!origin) {
              failed.push({ rowNumber: row.rowNumber, error: `Could not geocode origin "${row.data.origin_city}, ${row.data.origin_state}"` });
              return;
            }

            const destination = geoCache.get(destKey) ?? null;
            if (!destination) {
              failed.push({ rowNumber: row.rowNumber, error: `Could not geocode destination "${row.data.destination_city}, ${row.data.destination_state}"` });
              return;
            }

            const payload = buildLoadPayload(row, origin, destination, companyId!, user.id);
            const { data: newLoad, error } = await supabase.from("loads").insert(payload as any).select("id").single();
            if (error) {
              failed.push({ rowNumber: row.rowNumber, error: error.message });
              return;
            }

            supabase.functions.invoke("check-rate-alerts", { body: { load_id: newLoad.id } }).catch(() => {});
            posted++;
          })(),
          ROW_TIMEOUT_MS,
        );
      } catch (err: any) {
        failed.push({ rowNumber: row.rowNumber, error: err?.message || "Unknown error" });
      }
    }

    const summary: PostResult = {
      posted,
      skipped: result.invalidRows.length,
      failed,
      cancelled,
      total: result.totalRows,
    };
    setPostResult(summary);
    setPosting(false);
    setPostingProgress(null);

    const cancelledNote = cancelled > 0 ? ` · ${cancelled} cancelled` : "";
    toast({
      title: `${posted} load${posted !== 1 ? "s" : ""} posted`,
      description: failed.length > 0
        ? `${failed.length} failed${cancelledNote} — see details below`
        : `All valid rows posted successfully.${cancelledNote}`,
      variant: failed.length > 0 ? "destructive" : "default",
    });
  };

  /* ------------------------------------------------------------------ */
  /*  Cancel posting                                                     */
  /* ------------------------------------------------------------------ */

  const handleCancel = () => {
    cancelRef.current = true;
  };

  /* ------------------------------------------------------------------ */
  /*  Save as Bulk Template                                              */
  /* ------------------------------------------------------------------ */

  const handleSaveTemplate = async () => {
    if (!result || result.validRows.length === 0 || !user || !saveName.trim()) return;
    setSaving(true);

    let companyId: string | null = null;
    try {
      const { data } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      if (data && data.length > 0) companyId = data[0];
    } catch { /* optional */ }

    const rowsData = result.validRows.map((r) => r.data);

    const { error } = await supabase.from("bulk_templates" as any).insert({
      created_by: user.id,
      company_id: companyId,
      name: saveName.trim(),
      rows: rowsData,
      row_count: rowsData.length,
    } as any);

    setSaving(false);

    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Bulk template saved", description: `"${saveName.trim()}" with ${rowsData.length} row${rowsData.length !== 1 ? "s" : ""}` });
      setShowSaveInput(false);
      setSaveName("");
      fetchSavedTemplates();
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Use saved template                                                 */
  /* ------------------------------------------------------------------ */

  const handleUseTemplate = async (template: SavedBulkTemplate) => {
    const validRows: BulkRow[] = [];
    const invalidRows: { row: BulkRow; errors: string[] }[] = [];

    (template.rows as Record<string, string>[]).forEach((data, idx) => {
      const row: BulkRow = { rowNumber: idx + 1, data };
      const errors = validateRow(row);
      if (errors.length > 0) {
        invalidRows.push({ row, errors });
      } else {
        validRows.push(row);
      }
    });

    const totalRows = validRows.length + invalidRows.length;

    if (!checkRowLimit(totalRows)) return;

    setResult({ validRows, invalidRows, totalRows });
    setPostResult(null);
    setShowSaveInput(false);

    // Update usage_count and last_used_at
    await supabase
      .from("bulk_templates" as any)
      .update({ usage_count: template.usage_count + 1, last_used_at: new Date().toISOString() } as any)
      .eq("id", template.id);

    toast({ title: `Template "${template.name}" loaded`, description: `${validRows.length} valid · ${invalidRows.length} invalid` });
  };

  /* ------------------------------------------------------------------ */
  /*  Delete saved template                                              */
  /* ------------------------------------------------------------------ */

  const handleDeleteTemplate = async (id: string) => {
    const { error } = await supabase.from("bulk_templates" as any).delete().eq("id", id);
    if (!error) {
      setSavedTemplates((prev) => prev.filter((t) => t.id !== id));
      toast({ title: "Template deleted" });
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */

  const hasResult = result !== null;
  const hasPostResult = postResult !== null;
  const progressPct = postingProgress
    ? Math.round((postingProgress.current / postingProgress.total) * 100)
    : 0;

  return (
    <>
    <PostLoadCard>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full px-4 py-3">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <DemoHint hintKey="section.bulkUpload" side="right">
            <h4 className="text-sm font-semibold text-foreground">Bulk Upload Loads</h4>
          </DemoHint>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      <div
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: open ? (hasResult || hasPostResult ? 800 : 500) : 0,
          opacity: open ? 1 : 0,
        }}
      >
        <div className="px-4 pb-3 space-y-3">
          {/* ---- Instructions (initial state) ---- */}
          {!hasResult && !hasPostResult && (
            <>
              <p className="text-xs text-muted-foreground">
                Download the LaneRunner CSV template, fill in your loads, and upload to post in bulk.
                <span className="block mt-0.5 text-[10px]">Maximum {MAX_BULK_ROWS} rows per upload.</span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                >
                  <Download className="h-3 w-3" />
                  Download Template
                </button>
                <button
                  onClick={() => showWarningOrRun(() => fileRef.current?.click())}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                >
                  <Upload className="h-3 w-3" />
                  Upload CSV
                </button>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
              </div>
              <p className="text-[10px] text-muted-foreground">CSV only · one row = one load</p>

              {/* ---- Saved Bulk Templates ---- */}
              {savedTemplates.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FolderOpen className="h-3 w-3" />
                    <span className="font-semibold text-foreground">Saved Bulk Templates</span>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted">{savedTemplates.length}</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-0 rounded-md border border-border bg-muted/40">
                    {savedTemplates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between px-2.5 py-2 border-b border-border last:border-0">
                        <div className="flex items-center gap-2 text-xs text-foreground min-w-0">
                          <span className="font-semibold truncate">{t.name}</span>
                          <span className="text-muted-foreground">{t.row_count} row{t.row_count !== 1 ? "s" : ""}</span>
                          {t.last_used_at && (
                            <span className="text-[10px] text-muted-foreground hidden sm:inline">
                              last used {new Date(t.last_used_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => handleUseTemplate(t)}
                            className="text-[11px] font-semibold px-2 py-0.5 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground"
                          >
                            Use
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(t.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {loadingTemplates && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading saved templates…
                </div>
              )}
            </>
          )}

          {/* ---- Preview state (pre-post) ---- */}
          {hasResult && !hasPostResult && result && (
            <div className="space-y-2">
              {/* Large upload warning */}
              {rowLimitWarning && (
                <div className="flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                  {rowLimitWarning}
                </div>
              )}

              {/* Summary bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-semibold text-foreground">{result.totalRows} row{result.totalRows !== 1 ? "s" : ""}</span>
                  <span className="inline-flex items-center gap-1" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    <CheckCircle2 className="h-3 w-3" />
                    {result.validRows.length} valid
                  </span>
                  {result.invalidRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <AlertTriangle className="h-3 w-3" />
                      {result.invalidRows.length} invalid
                    </span>
                  )}
                </div>
                <button onClick={clearAll} disabled={posting} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 disabled:opacity-50">
                  <X className="h-3 w-3" /> Clear
                </button>
              </div>

              {/* Invalid rows detail */}
              {result.invalidRows.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border border-border bg-muted/40 p-2">
                  {result.invalidRows.map((ir) => (
                    <div key={ir.row.rowNumber} className="text-[10px]">
                      <span className="font-semibold text-destructive">Row {ir.row.rowNumber}:</span>
                      {ir.errors.map((err, idx) => (
                        <span key={idx} className="ml-1.5 text-muted-foreground">{err}{idx < ir.errors.length - 1 ? ";" : ""}</span>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Valid rows note */}
              {result.validRows.length > 0 && !posting && (
                <div className="rounded-md border border-border bg-muted/40 p-2">
                  <p className="text-[10px] text-muted-foreground">
                    {result.validRows.length} load{result.validRows.length !== 1 ? "s" : ""} ready to post.
                    {result.invalidRows.length > 0 ? " Invalid rows will be skipped." : ""}
                  </p>
                </div>
              )}

              {/* Progress bar during posting */}
              {posting && postingProgress && (
                <div className="space-y-1.5">
                  <Progress value={progressPct} className="h-2" />
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">
                      Posting row {postingProgress.current} of {postingProgress.total}…
                    </p>
                    <button
                      onClick={handleCancel}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors inline-flex items-center gap-1"
                    >
                      <Ban className="h-3 w-3" />
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {!posting && (
                <div className="flex items-center gap-2 flex-wrap">
                  {result.validRows.length > 0 && (
                    <button
                      onClick={() => showWarningOrRun(() => handlePost())}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded-md transition-colors text-primary-foreground inline-flex items-center gap-1.5"
                      style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Post Valid Loads
                    </button>
                  )}
                  <button
                    onClick={() => showWarningOrRun(() => fileRef.current?.click())}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                  >
                    <Upload className="h-3 w-3" /> Upload New CSV
                  </button>
                  {result.validRows.length > 0 && !showSaveInput && (
                    <button
                      onClick={() => setShowSaveInput(true)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                    >
                      <Save className="h-3 w-3" /> Save as Template
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                </div>
              )}

              {/* Save template input */}
              {showSaveInput && !posting && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Template name…"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    className="text-[11px] px-2 py-1 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-ring"
                    onKeyDown={(e) => e.key === "Enter" && handleSaveTemplate()}
                  />
                  <button
                    onClick={handleSaveTemplate}
                    disabled={saving || !saveName.trim()}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors text-primary-foreground inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save
                  </button>
                  <button onClick={() => { setShowSaveInput(false); setSaveName(""); }} className="text-muted-foreground hover:text-foreground p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ---- Post result summary ---- */}
          {hasPostResult && postResult && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-foreground">Bulk Post Complete</p>

              <div className="flex items-center gap-3 text-xs flex-wrap">
                <span className="font-semibold text-foreground">{postResult.total} total</span>
                <span className="inline-flex items-center gap-1" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  <CheckCircle2 className="h-3 w-3" /> {postResult.posted} posted
                </span>
                {postResult.skipped > 0 && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <AlertTriangle className="h-3 w-3" /> {postResult.skipped} skipped
                  </span>
                )}
                {postResult.failed.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-destructive">
                    <X className="h-3 w-3" /> {postResult.failed.length} failed
                  </span>
                )}
                {postResult.cancelled > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <Ban className="h-3 w-3" /> {postResult.cancelled} cancelled
                  </span>
                )}
              </div>

              {postResult.failed.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border border-border bg-muted/40 p-2">
                  {postResult.failed.map((f) => (
                    <div key={f.rowNumber} className="text-[10px]">
                      <span className="font-semibold text-destructive">Row {f.rowNumber}:</span>
                      <span className="ml-1.5 text-muted-foreground">{f.error}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Save as template after posting */}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={clearAll} className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5">
                  Done
                </button>
                {result && result.validRows.length > 0 && !showSaveInput && (
                  <button
                    onClick={() => setShowSaveInput(true)}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground inline-flex items-center gap-1.5"
                  >
                    <Save className="h-3 w-3" /> Save as Template
                  </button>
                )}
              </div>

              {showSaveInput && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Template name…"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    className="text-[11px] px-2 py-1 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-ring"
                    onKeyDown={(e) => e.key === "Enter" && handleSaveTemplate()}
                  />
                  <button
                    onClick={handleSaveTemplate}
                    disabled={saving || !saveName.trim()}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors text-primary-foreground inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save
                  </button>
                  <button onClick={() => { setShowSaveInput(false); setSaveName(""); }} className="text-muted-foreground hover:text-foreground p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PostLoadCard>
    <VerificationWarningModal {...modalProps} />
    </>
  );
}
